import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, LockKey } from "@phosphor-icons/react";
import api from "../lib/api";

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/prelaunch/waitlist", { email });
      setDone(true);
    } catch {
      toast.error("Please enter a valid email address");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p className="flex items-center gap-2 text-[#2B5329] font-semibold" data-testid="waitlist-success">
        <CheckCircle size={22} weight="fill" /> You're on the list — we'll email you on launch day.
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 w-full max-w-lg" data-testid="waitlist-form">
      <input
        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com" data-testid="waitlist-email-input"
        className="w-full sm:flex-1 h-14 shrink-0 px-6 rounded-full bg-white border border-[#E5E2D9] focus:border-[#A94438] focus:outline-none transition-colors"
      />
      <button
        type="submit" disabled={busy} data-testid="waitlist-submit-button"
        className="h-14 px-8 rounded-full bg-[#A94438] hover:bg-[#8A372E] text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
      >
        {busy ? "Joining…" : "Notify me"} <ArrowRight size={18} weight="bold" />
      </button>
    </form>
  );
}

function AccessCodeForm({ onUnlocked }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/prelaunch/unlock", { code });
      onUnlocked();
    } catch {
      toast.error("That access code isn't right");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="access-code-toggle"
        className="flex items-center gap-2 text-sm text-[#5C5C54] hover:text-[#1A1A18] transition-colors">
        <LockKey size={16} weight="duotone" /> Team access
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid="access-code-form">
      <input
        autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Access code"
        data-testid="access-code-input"
        className="h-11 px-4 rounded-full bg-white border border-[#E5E2D9] focus:border-[#2B5329] focus:outline-none text-sm uppercase tracking-wider"
      />
      <button type="submit" disabled={busy || !code.trim()} data-testid="access-code-submit-button"
        className="h-11 px-5 rounded-full bg-[#2B5329] text-white text-sm font-semibold disabled:opacity-60">
        {busy ? "…" : "Enter"}
      </button>
    </form>
  );
}

export default function ComingSoon({ onUnlocked }) {
  return (
    <div className="min-h-screen bg-[#F9F7F3] flex flex-col px-6 md:px-16 py-8 lg:pr-[46%] relative" data-testid="coming-soon-page">
      <div className="hidden lg:block absolute inset-y-0 right-0 w-[42%]">
        <img src="https://images.unsplash.com/photo-1734255026082-82fdc81991f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="" className="h-full w-full object-cover rounded-l-[3rem]" data-testid="coming-soon-hero-image" />
      </div>
      <img src="/afripantry-logo.png" alt="AfriPantry" className="h-8 md:h-10 w-auto self-start" data-testid="coming-soon-logo" />
      <main className="flex-1 flex flex-col justify-center max-w-3xl py-16">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#A94438] mb-6">Launching soon across the UK</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#1A1A18] leading-[1.05] mb-6">
          The taste of home,<br />
          <span className="font-editorial italic font-medium text-[#2B5329]">delivered to your door.</span>
        </h1>
        <p className="text-base md:text-lg text-[#5C5C54] max-w-xl mb-10">
          African groceries from trusted independent vendors — all in one basket. Be the first to know when we open.
        </p>
        <WaitlistForm />
      </main>
      <footer className="flex flex-wrap items-center justify-between gap-4 text-xs text-[#5C5C54]">
        <span>© {new Date().getFullYear()} AfriPantry · <Link to="/privacy" className="underline" data-testid="coming-soon-privacy-link">Privacy</Link></span>
        <AccessCodeForm onUnlocked={onUnlocked} />
      </footer>
    </div>
  );
}
